import {Injectable} from '@angular/core';
import {TenantService} from '../tenant.service';

/**
 * Theme name the CXone container reports for Lyra.
 */
export const LYRA_THEME = 'lyra';

/**
 * Class placed on <body> while the Lyra theme is active. Global overrides in
 * styles.scss hang off this class, so nothing changes unless it is present.
 */
export const LYRA_BODY_CLASS = 'esp-theme-lyra';

/**
 * Theme payload as sent by the CXone MFE wrapper, both on the `auth` message
 * (under `theme`) and as the whole payload of a `theme` message.
 */
export interface ThemeInfo {
  theme: string | null;
  isLyra: boolean;
}

/**
 * Applies the theme the CXone container reports to the ESP document.
 *
 * ESP runs inside an iframe, so it cannot read the container's `data-theme`
 * attribute itself - the MFE wrapper watches that attribute and forwards the
 * result over the bridge. This service turns that into a single class on
 * <body>, which the stylesheets key off.
 *
 * Theming is deliberately limited to CXone mode. The standalone (IEX/WCX)
 * app has its own chrome and is not part of the Lyra migration, so a theme
 * message there is recorded but never applied.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private _theme: string | null = null;
  private _isLyra = false;

  constructor(private tenantService: TenantService) {}

  /** Theme name last reported by the container, or null when none was declared. */
  get theme(): string | null {
    return this._theme;
  }

  /** True when the container reported Lyra AND we are running in CXone mode. */
  get isLyra(): boolean {
    return this._isLyra && this.tenantService.isCXOne;
  }

  /**
   * Records the reported theme and syncs the <body> class.
   *
   * Tolerates a missing or malformed payload: anything we cannot read as a
   * theme leaves ESP on its default (pre-Lyra) look rather than throwing, so
   * an older MFE that sends no theme at all keeps working.
   */
  applyTheme(info: ThemeInfo | null | undefined): void {
    this._theme = null;
    this._isLyra = false;

    if (info && typeof info === 'object') {
      this._theme = typeof info.theme === 'string' ? info.theme : null;
      // The contract says to branch on isLyra rather than string-matching the
      // theme name, so new themes need no ESP change. Fall back to the name
      // only when isLyra is absent, so a partial payload still works.
      this._isLyra = typeof info.isLyra === 'boolean' ? info.isLyra : this._theme === LYRA_THEME;
    }

    this.syncBodyClass();
  }

  private syncBodyClass(): void {
    if (!document.body) {
      return;
    }
    document.body.classList.toggle(LYRA_BODY_CLASS, this.isLyra);
  }
}
